import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { Subscription } from 'rxjs';

import { Model3dService, ModelRecord, UploadedModel } from '@services/model-3d.service';
import { SharedService } from '@services/shared.service';
import { ModelPreviewComponent } from '@shared/model-preview/model-preview.component';

// Flattened view of a /my_models record — the API nests the file inside the record,
// so the template gets a ready-to-render row instead of digging through it each time.
interface ModelRow {
  id?: number | string;
  title: string;
  fileName: string;
  size: number;
  createdAt: number | string | null;
  url: string;
  raw: ModelRecord;
}

@Component({
  selector: 'app-model-3d',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, NzToolTipModule, NzModalModule, ModelPreviewComponent],
  templateUrl: './model-3d.component.html',
  styleUrls: ['./model-3d.component.scss'],
})
export class Model3dComponent implements OnInit, OnDestroy {
  @ViewChild('modelFileInput') modelFileInput!: ElementRef<HTMLInputElement>;

  private modelService = inject(Model3dService);
  private sharedService = inject(SharedService);
  private translate = inject(TranslateService);
  private cdRef = inject(ChangeDetectorRef);
  private modal = inject(NzModalService);

  // Only .glb for now — it's the single format the contest AR viewer consumes.
  readonly allowedExtensions = ['glb'];
  readonly maxSizeMb = 20; // the gateway takes the file as base64 inside one JSON request
  readonly acceptAttr = '.glb';

  selectedFile: File | null = null;
  modelName = '';
  isDragOver = false;
  isUploading = false;
  uploadProgress = 0;

  // `uploadedModel` is the raw record (shown in the JSON viewer); `uploadedFile` is the
  // nested file object it wraps — that's where name/size/mime actually live.
  uploadedModel: ModelRecord | null = null;
  uploadedFile: UploadedModel | null = null;
  uploadedModelUrl = '';
  showRawResponse = false;

  models: ModelRow[] = [];
  isLoadingModels = false;
  deletingId: number | string | null = null;

  // 3D preview popup — rendering lives in the shared ModelPreviewComponent.
  isPreviewOpen = false;
  previewSourceUrl = '';
  previewTitle = '';
  previewSize = 0;

  private uploadSub?: Subscription;
  private listSub?: Subscription;

  ngOnInit(): void {
    this.loadModels();
  }

  ngOnDestroy(): void {
    this.uploadSub?.unsubscribe();
    this.listSub?.unsubscribe();
  }

  /* ---------------------------------------------------------------------- */
  /*                            My models (GET)                             */
  /* ---------------------------------------------------------------------- */

  loadModels(): void {
    this.isLoadingModels = true;
    this.listSub?.unsubscribe();

    this.listSub = this.modelService.getMyModels().subscribe({
      next: (res) => {
        const list = this.modelService.toRecordList(res);
        this.models = list.map((record) => this.toRow(record));
        this.isLoadingModels = false;
        this.cdRef.detectChanges();
      },
      error: () => {
        this.isLoadingModels = false;
        // Not the server's text — a bare "Invalid token." tells the user nothing.
        this.sharedService.createNotification('error', this.translate.instant('model3d.load-failed'));
        this.cdRef.detectChanges();
      },
    });
  }

  confirmDelete(row: ModelRow): void {
    if (row.id == null) return;
    this.modal.confirm({
      nzTitle: this.translate.instant('model3d.delete-title'),
      nzContent: this.translate.instant('model3d.delete-confirm', { name: row.title }),
      nzOkText: this.translate.instant('model3d.delete-ok'),
      nzOkDanger: true,
      nzCancelText: this.translate.instant('model3d.cancel'),
      nzClassName: 'contest-styled-modal',
      nzCentered: true,
      nzOnOk: () => this.deleteModel(row),
    });
  }

  private deleteModel(row: ModelRow): void {
    this.deletingId = row.id!;
    this.modelService.deleteModel(row.id!).subscribe({
      next: () => {
        this.deletingId = null;
        this.models = this.models.filter((m) => m.id !== row.id);
        if (this.uploadedModel?.id === row.id) this.resetResult();
        this.sharedService.createNotification('success', this.translate.instant('model3d.deleted'));
        this.cdRef.detectChanges();
      },
      error: (err: any) => {
        this.deletingId = null;
        this.sharedService.createNotification(
          'error',
          err?.error?.message || this.translate.instant('model3d.delete-failed'),
        );
        this.cdRef.detectChanges();
      },
    });
  }

  private resetResult(): void {
    this.uploadedModel = null;
    this.uploadedFile = null;
    this.uploadedModelUrl = '';
    this.showRawResponse = false;
  }

  private toRow(record: ModelRecord): ModelRow {
    const fileObject = this.modelService.getFileObject(record);
    return {
      id: record?.id,
      title: (record?.name || '').trim() || fileObject?.name || this.translate.instant('model3d.untitled'),
      fileName: fileObject?.name || '',
      size: fileObject?.size ?? 0,
      createdAt: record?.created_at ?? null,
      url: this.modelService.getModelUrl(record),
      raw: record,
    };
  }

  /* ---------------------------------------------------------------------- */
  /*                              File selection                            */
  /* ---------------------------------------------------------------------- */

  triggerFileInput(): void {
    this.modelFileInput?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.setFile(input.files[0]);
    }
    // Reset so re-picking the same file still fires (change).
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isUploading) {
      this.isDragOver = true;
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    if (this.isUploading) return;

    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.setFile(file);
    }
  }

  private setFile(file: File): void {
    const extension = this.getExtension(file.name);

    if (!this.allowedExtensions.includes(extension)) {
      this.translate
        .get('model3d.invalid-format', { formats: this.allowedExtensions.join(', ') })
        .subscribe((msg: string) => this.sharedService.createNotification('error', msg));
      return;
    }

    if (file.size > this.maxSizeMb * 1024 * 1024) {
      this.translate
        .get('model3d.too-large', { size: this.maxSizeMb })
        .subscribe((msg: string) => this.sharedService.createNotification('error', msg));
      return;
    }

    this.selectedFile = file;
    // Prefill the name from the file so the record is never saved nameless.
    if (!this.modelName.trim()) {
      this.modelName = file.name.replace(/\.[^.]+$/, '');
    }
    this.uploadProgress = 0;
    this.cdRef.detectChanges();
  }

  clearFile(): void {
    if (this.isUploading) return;
    this.selectedFile = null;
    this.modelName = '';
    this.uploadProgress = 0;
  }

  /* ---------------------------------------------------------------------- */
  /*                             Upload (POST)                              */
  /* ---------------------------------------------------------------------- */

  uploadModel(): void {
    if (!this.selectedFile || this.isUploading) return;

    const file = this.selectedFile;
    this.isUploading = true;
    this.uploadProgress = 0;
    this.uploadedModel = null;
    this.uploadedFile = null;
    this.uploadedModelUrl = '';

    this.uploadSub = this.modelService.uploadModel(file, this.modelName.trim()).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          this.uploadProgress = Math.round((event.loaded / event.total) * 100);
          this.cdRef.detectChanges();
        } else if (event.type === HttpEventType.Response) {
          this.isUploading = false;
          this.uploadProgress = 100;

          const response = (event.body || {}) as ModelRecord;
          this.uploadedModel = response;
          this.uploadedFile = this.modelService.getFileObject(response);
          this.uploadedModelUrl = this.modelService.getModelUrl(response);
          this.selectedFile = null;
          this.modelName = '';

          this.translate
            .get('model3d.upload-success')
            .subscribe((msg: string) => this.sharedService.createNotification('success', msg));

          // Pull the server list back in so the new record shows with its real id/date.
          this.loadModels();
          this.cdRef.detectChanges();
        }
      },
      error: (err: HttpErrorResponse) => {
        this.isUploading = false;
        this.uploadProgress = 0;
        this.sharedService.showError(err, this.translate.instant('model3d.upload-failed'));
        this.cdRef.detectChanges();
      },
    });
  }

  cancelUpload(): void {
    this.uploadSub?.unsubscribe();
    this.isUploading = false;
    this.uploadProgress = 0;
  }

  /* ---------------------------------------------------------------------- */
  /*                                 Actions                                */
  /* ---------------------------------------------------------------------- */

  // Hands the model to the shared preview popup, which renders it in-app instead of hitting
  // the vault URL directly — the file is served as application/octet-stream, so a plain link
  // would only ever download it.
  openPreview(url: string, title: string, size = 0): void {
    if (!url) {
      this.notifyMissingUrl();
      return;
    }

    this.previewTitle = title;
    this.previewSize = size;
    this.previewSourceUrl = url;
    this.isPreviewOpen = true;
  }

  // Copies the URL text only — it must never navigate to (and therefore download) the file.
  copyUrl(url: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!url) {
      this.notifyMissingUrl();
      return;
    }

    this.sharedService.copyToClipboard(url);
  }

  private notifyMissingUrl(): void {
    this.translate
      .get('model3d.no-url')
      .subscribe((msg: string) => this.sharedService.createNotification('warning', msg));
  }

  toggleRawResponse(): void {
    this.showRawResponse = !this.showRawResponse;
  }

  get rawResponseJson(): string {
    return this.uploadedModel ? JSON.stringify(this.uploadedModel, null, 2) : '';
  }

  trackByRow = (_: number, row: ModelRow) => row.id ?? row.url;

  getExtension(name: string): string {
    return (name.split('.').pop() || '').toLowerCase();
  }

  formatSize(bytes: number): string {
    if (!bytes && bytes !== 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
}
